<template>
  <div
    class="comment-item"
    :class="{ 'comment-reply': comment.is_reply }"
  >
    <div class="comment-bubble">
      <!-- Header with Name and Avatar -->
      <div class="comment-header">
        <div class="header-content">
          <strong class="comment-user-name">{{ userName }}</strong>
          <b-avatar
            v-if="comment.user && comment.user.avatar"
            :src="comment.user.avatar"
            size="32"
            class="header-avatar"
          />
          <b-avatar
            v-else
            variant="success"
            size="32"
            class="header-avatar"
          >
            {{ userInitial }}
          </b-avatar>
        </div>
        <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
      </div>

      <!-- Comment Text -->
      <div
        v-if="!isEditing"
        class="comment-text"
      >
        {{ comment.comment }}
      </div>

      <!-- Edit Form -->
      <b-form
        v-else
        class="edit-form"
        @submit.prevent="saveEdit"
      >
        <b-form-textarea
          v-model="editText"
          rows="2"
          maxlength="2000"
          class="edit-textarea"
        />
        <div class="edit-actions">
          <button
            type="button"
            class="cancel-btn"
            @click="cancelEdit"
          >
            إلغاء
          </button>
          <button
            type="submit"
            class="save-btn"
            :disabled="!editText.trim() || saving"
          >
            حفظ
          </button>
        </div>
      </b-form>

      <!-- Actions Bar -->
      <div class="comment-actions-bar">
        <button
          type="button"
          class="action-link reply-btn"
          @click="$emit('reply', comment.id)"
        >
          رد
        </button>
        <span
          v-if="canEditOrDelete"
          class="action-separator"
        >·</span>
        <button
          v-if="canEdit"
          type="button"
          class="action-link edit-btn"
          @click="$emit('edit', comment)"
        >
          تعديل
        </button>
        <span
          v-if="canEdit && canDelete"
          class="action-separator"
        >·</span>
        <button
          v-if="canDelete"
          type="button"
          class="action-link delete-btn"
          @click="$emit('delete', comment.id)"
        >
          حذف
        </button>
      </div>
    </div>

    <!-- Replies -->
    <div
      v-if="comment.replies && comment.replies.length > 0"
      class="replies"
    >
      <CommentItem
        v-for="reply in comment.replies"
        :key="reply.id"
        :comment="reply"
        :task-id="taskId"
        :current-user-id="currentUserId"
        @reply="$emit('reply', $event)"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
        @refresh="$emit('refresh')"
      />
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BButton,
  BForm,
  BFormTextarea,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import Swal from 'sweetalert2'

const CommentItem = {
  name: 'CommentItem',
  components: {
    BCard,
    BAvatar,
    BBadge,
    BButton,
    BForm,
    BFormTextarea,
    FeatherIcon,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    taskId: {
      type: [Number, String],
      required: true,
    },
    currentUserId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      isEditing: false,
      editText: '',
      saving: false,
    }
  },
  computed: {
    canEdit() {
      return this.currentUserId && this.comment.user_id === this.currentUserId
    },
    canDelete() {
      return (
        this.currentUserId
        && (this.comment.user_id === this.currentUserId || this.hasManageTasksPermission())
      )
    },
    canEditOrDelete() {
      return this.canEdit || this.canDelete
    },
    userName() {
      return (this.comment.user && this.comment.user.name) || 'مستخدم'
    },
    userInitial() {
      if (this.comment.user && this.comment.user.name) {
        return this.comment.user.name[0] || 'U'
      }
      return 'U'
    },
  },
  methods: {
    ...mapActions('taskComments', ['updateComment']),
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)

      if (minutes < 1) return 'الآن'
      if (minutes < 60) return `منذ ${minutes} دقيقة`
      if (hours < 24) return `منذ ${hours} ساعة`
      if (days < 7) return `منذ ${days} يوم`
      return date.toLocaleDateString('ar-EG')
    },
    hasManageTasksPermission() {
      try {
        const abilities = JSON.parse(localStorage.getItem('userAbilities') || '[]')
        return abilities.includes('manage-tasks')
      } catch (e) {
        return false
      }
    },
    async saveEdit() {
      if (!this.editText.trim() || this.saving) return

      this.saving = true
      try {
        await this.updateComment({
          commentId: this.comment.id,
          comment: this.editText.trim(),
        })
        this.isEditing = false
        this.editText = ''
        this.$toast.success('تم تحديث التعليق بنجاح')
        this.$emit('refresh')
      } catch (error) {
        this.$toast.error('فشل في تحديث التعليق')
      } finally {
        this.saving = false
      }
    },
    cancelEdit() {
      this.isEditing = false
      this.editText = ''
    },
  },
  watch: {
    comment: {
      handler(newComment) {
        if (this.isEditing) {
          this.editText = newComment.comment
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.$listeners.edit) {
      this.$on('edit', comment => {
        if (comment.id === this.comment.id) {
          this.isEditing = true
          this.editText = this.comment.comment
        }
      })
    }
  },
}

// Register self for recursion
CommentItem.components.CommentItem = CommentItem

export default CommentItem
</script>

<style scoped>
/* Enhanced Comment Style - Light Grey Bubbles */
.comment-item {
  position: relative;
  margin-bottom: 12px;
  padding: 0;
}

.comment-bubble {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  position: relative;
  max-width: 100%;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8e8e8;
}

.comment-reply .comment-bubble {
  background-color: #f5f5f5;
  margin-right: 16px;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.comment-user-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #000000;
  line-height: 1.4;
  margin: 0;
  padding: 0;
}

.header-avatar {
  flex-shrink: 0;
}

.header-avatar ::v-deep .avatar {
  background-color: #06284D !important;
  color: #fff !important;
  font-weight: 600;
  border: none;
}

.comment-date {
  font-size: 0.8125rem;
  color: #666666;
  line-height: 1.4;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}

.comment-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
  font-size: 0.9375rem;
  color: #333333;
  margin: 8px 0;
  padding: 0;
}

.comment-actions-bar {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  gap: 4px;
  flex-wrap: wrap;
  border-top: 1px solid #e0e0e0;
}

.action-link {
  background: none;
  border: none;
  color: #06284D;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 4px 8px;
  margin: 0;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s, color 0.2s;
  line-height: 1.4;
  font-family: inherit;
}

.action-link:hover {
  background-color: #e8f5e9;
  color: #06284D;
}

.action-link:active {
  background-color: #c8e6c9;
}

.delete-btn:hover {
  color: #d32f2f;
  background-color: #ffebee;
}

.action-separator {
  color: #999999;
  font-size: 0.8125rem;
  padding: 0 2px;
  line-height: 1.4;
}

.replies {
  margin-top: 12px;
  margin-right: 16px;
  padding-right: 16px;
  border-right: 2px solid #e0e0e0;
}

.edit-form {
  margin-top: 8px;
}

.edit-textarea {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9375rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  background-color: #fff;
}

.edit-textarea:focus {
  outline: none;
  border-color: #06284D;
  box-shadow: 0 0 0 2px rgba(6, 40, 77, 0.2);
}

.edit-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-end;
}

.cancel-btn,
.save-btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  min-height: 36px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background-color: #e0e0e0;
  color: #333;
}

.cancel-btn:hover {
  background-color: #d0d0d0;
}

.save-btn {
  background-color: #06284D;
  color: #fff;
}

.save-btn:hover:not(:disabled) {
  background-color: #003d3a;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .comment-item {
    margin-bottom: 10px;
  }

  .comment-bubble {
    padding: 10px 12px;
    border-radius: 6px;
  }

  .comment-header {
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .header-content {
    gap: 6px;
  }

  .header-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }

  .comment-user-name {
    font-size: 0.875rem;
  }

  .comment-date {
    font-size: 0.75rem;
  }

  .comment-text {
    font-size: 0.875rem;
    margin: 6px 0;
  }

  .comment-actions-bar {
    margin-top: 6px;
    padding-top: 6px;
    gap: 2px;
  }

  .action-link {
    font-size: 0.75rem;
    padding: 4px 6px;
    min-height: 32px;
  }

  .edit-textarea {
    font-size: 16px;
    padding: 8px 12px;
  }

  .edit-actions {
    flex-direction: column;
    gap: 8px;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
    min-height: 40px;
  }

  .replies {
    margin-right: 12px;
    padding-right: 12px;
  }
}

@media (max-width: 576px) {
  .comment-item {
    margin-bottom: 8px;
  }

  .comment-bubble {
    padding: 8px 10px;
    border-radius: 6px;
  }

  .header-avatar {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }

  .comment-user-name {
    font-size: 0.8125rem;
  }

  .comment-date {
    font-size: 0.6875rem;
  }

  .comment-text {
    font-size: 0.8125rem;
    margin: 4px 0;
  }

  .comment-actions-bar {
    margin-top: 4px;
    padding-top: 4px;
  }

  .action-link {
    font-size: 0.6875rem;
    padding: 3px 5px;
    min-height: 28px;
  }

  .replies {
    margin-right: 8px;
    padding-right: 8px;
  }
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .action-link {
    min-height: 44px;
    padding: 8px 12px;
  }

  .comment-bubble {
    padding: 10px 14px;
  }

  .cancel-btn,
  .save-btn {
    min-height: 44px;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  .comment-avatar {
    width: 24px;
    height: 24px;
  }

  .comment-bubble {
    padding: 6px 8px;
    border-radius: 14px;
  }

  .comment-user-name {
    font-size: 0.75rem;
  }

  .comment-date {
    font-size: 0.625rem;
  }

  .comment-text {
    font-size: 0.75rem;
  }

  .action-link {
    font-size: 0.625rem;
    padding: 4px 6px;
    min-height: 32px;
  }
}
</style>
